import { PlusIcon, Squares2X2Icon } from '@heroicons/react/24/outline'
import { isEqual } from 'lodash'
import { useMemo, useState, type ReactElement } from 'react'
import { SectionTable, SectionTableTrashButton } from 'app/components/table/SectionTable'
import { Button } from 'app/components/ui/button'
import { Checkbox } from 'app/components/ui/checkbox'
import { Input } from 'app/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { Switch } from 'app/components/ui/switch'
import { useAIAgentStore } from 'app/stores/StoreContext'
import { useSettingsSection } from './useSettingsSave'
import type { EditableOffer, EditableService } from './useAiAgentOverview'

const PATIENT_STATUSES = [
  { value: `New Patient`, label: `New patients` },
  { value: `Existing Patient`, label: `Existing patients` },
]
const INSURANCE = [
  { value: `Any`, label: `Any insurance` },
  { value: `Insured`, label: `Insured only` },
  { value: `Uninsured`, label: `Uninsured only` },
]
const CONSULT_HINT = `Check this when the service starts with a consult. Add a fee for a paid consult, or leave it at $0 for a free one.`
const VISITS = [
  { value: `any`, label: `Any visit` },
  { value: `emergency`, label: `Emergencies only` },
]
const NEW_OFFER: EditableOffer = {
  name: ``,
  patientStatus: `New Patient`,
  eligibleInsurance: `Any`,
  onlyEmergencyEligible: false,
}
const NEW_SERVICE: EditableService = {
  name: ``,
  lowPrice: ``,
  highPrice: ``,
  consultRequired: false,
  consultPrice: ``,
  offered: true,
}

/** The starter list Prepopulate adds, the same one the AI Agent configuration page offered. */
const DEFAULT_SERVICES: [name: string, low: number, high: number, consult: boolean][] = [
  [`Cleaning`, 100, 200, false],
  [`Exam`, 75, 150, false],
  [`X-Ray`, 50, 250, false],
  [`Filling`, 150, 300, false],
  [`Root Canal`, 800, 1500, false],
  [`Crown`, 800, 1500, false],
  [`Extraction`, 150, 450, false],
  [`Emergency Exam`, 100, 200, false],
  [`Whitening`, 75, 500, true],
  [`Invisalign`, 1500, 7000, true],
  [`Implant`, 1500, 7000, true],
  [`Veneers`, 250, 3000, true],
  [`Bonding (per tooth)`, 100, 900, true],
]

/** A draft row and the saved row it started from; new rows have no origin. */
type Tracked<T> = T & { origin?: number }

function untracked<T>({ origin: _origin, ...row }: Tracked<T>): T {
  return row as T
}

const money = (value: string) => (value.trim() === `` ? null : Number(value))

function OptionSelect({
  value,
  options,
  onChange,
  label,
}: {
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  label: string
}): ReactElement {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={label} className="h-9 rounded-lg border-default bg-surface shadow-none">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * Settings' Offers and Services, edited in place. Each table saves when the page's save bar saves.
 */
export function AiAgentOffersServices({
  locationId,
  editable,
}: {
  locationId: string
  editable: { offers: EditableOffer[]; services: EditableService[] }
}): ReactElement {
  const agentStore = useAIAgentStore()
  const [offerDraft, setOfferDraft] = useState<Tracked<EditableOffer>[] | null>(null)
  const [serviceDraft, setServiceDraft] = useState<Tracked<EditableService>[] | null>(null)
  const savedOffers = useMemo(() => editable.offers.map((row, origin) => ({ ...row, origin })), [editable.offers])
  const savedServices = useMemo(() => editable.services.map((row, origin) => ({ ...row, origin })), [editable.services])
  const offerRows = offerDraft ?? savedOffers
  const services = serviceDraft ?? savedServices
  const offerChanged = (keys: (keyof EditableOffer)[]) => (row: Tracked<EditableOffer>) => {
    const was = row.origin == null ? undefined : editable.offers[row.origin]
    return !!was && keys.some((key) => was[key] !== row[key])
  }
  const serviceChanged = (keys: (keyof EditableService)[]) => (row: Tracked<EditableService>) => {
    const was = row.origin == null ? undefined : editable.services[row.origin]
    return !!was && keys.some((key) => was[key] !== row[key])
  }

  useSettingsSection(`offers`, {
    dirty: !!offerDraft && !isEqual(offerDraft.map(untracked), editable.offers),
    discard: () => setOfferDraft(null),
    save: () =>
      agentStore.updateConfig(locationId, {
        offers: offerRows
          .filter((offer) => offer.name.trim())
          .map((offer) => ({ ...untracked(offer), name: offer.name.trim() })),
      }),
  })

  useSettingsSection(`services`, {
    dirty: !!serviceDraft && !isEqual(serviceDraft.map(untracked), editable.services),
    discard: () => setServiceDraft(null),
    save: () => {
      const rows = services
        .filter((service) => service.name.trim())
        .map((service) => ({
          offered: service.offered,
          input: {
            name: service.name.trim(),
            lowPrice: money(service.lowPrice),
            highPrice: money(service.highPrice),
            consultRequired: service.consultRequired,
            consultPrice: service.consultRequired ? money(service.consultPrice) : null,
          },
        }))
      return agentStore.updateConfig(locationId, {
        services: rows.filter((row) => row.offered).map((row) => row.input),
        servicesNotOffered: rows.filter((row) => !row.offered).map((row) => row.input),
      })
    },
  })

  const addButton = (noun: string, add: () => void) => (
    <Button
      type="button"
      variant="ghost"
      className="h-10 w-full rounded-none text-subtle hover:text-strong"
      onClick={add}
    >
      <PlusIcon className="size-4" />
      Add {noun}
    </Button>
  )

  const offerRow = (index: number, patch: Partial<EditableOffer>) =>
    setOfferDraft(offerRows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  const serviceRow = (index: number, patch: Partial<EditableService>) =>
    setServiceDraft(services.map((row, i) => (i === index ? { ...row, ...patch } : row)))

  return (
    <>
      <SectionTable<Tracked<EditableOffer>>
        attached
        title="Offers"
        footer={addButton(`offer`, () => setOfferDraft([...offerRows, { ...NEW_OFFER }]))}
        columns={[
          {
            header: `Offer`,
            changed: offerChanged([`name`]),
            width: `w-88`,
            render: (offer, index) => (
              <Input
                className="shadow-none"
                aria-label="Offer name"
                placeholder="e.g. Free whitening consultation"
                value={offer.name}
                onChange={(event) => offerRow(index, { name: event.target.value })}
              />
            ),
          },
          {
            header: `Patients`,
            changed: offerChanged([`patientStatus`]),
            width: `w-44`,
            render: (offer, index) => (
              <OptionSelect
                label="Patients"
                value={offer.patientStatus}
                options={PATIENT_STATUSES}
                onChange={(patientStatus) => offerRow(index, { patientStatus })}
              />
            ),
          },
          {
            header: `Insurance`,
            changed: offerChanged([`eligibleInsurance`]),
            width: `w-44`,
            render: (offer, index) => (
              <OptionSelect
                label="Insurance"
                value={offer.eligibleInsurance}
                options={INSURANCE}
                onChange={(eligibleInsurance) => offerRow(index, { eligibleInsurance })}
              />
            ),
          },
          {
            header: `Emergencies`,
            changed: offerChanged([`onlyEmergencyEligible`]),
            render: (offer, index) => (
              <div className="w-40">
                <OptionSelect
                  label="Emergencies"
                  value={offer.onlyEmergencyEligible ? `emergency` : `any`}
                  options={VISITS}
                  onChange={(visits) => offerRow(index, { onlyEmergencyEligible: visits === `emergency` })}
                />
              </div>
            ),
          },
          {
            header: ``,
            width: `w-14`,
            render: (_, index) => (
              <SectionTableTrashButton onClick={() => setOfferDraft(offerRows.filter((__, i) => i !== index))} />
            ),
          },
        ]}
        data={offerRows}
        rowKey={(_, index) => index}
        isNewRow={(row) => row.origin == null}
        emptyMessage="No offers. Add one for the agent to mention to callers."
      />

      <SectionTable<Tracked<EditableService>>
        attached
        title="Services"
        headerButtons={[
          {
            label: `Prepopulate`,
            icon: Squares2X2Icon,
            variant: `secondary`,
            onClick: () => {
              const listed = new Set(services.map((service) => service.name.trim().toLowerCase()))
              setServiceDraft([
                ...services,
                ...DEFAULT_SERVICES.filter(([name]) => !listed.has(name.toLowerCase())).map(
                  ([name, low, high, consult]) => ({
                    name,
                    lowPrice: String(low),
                    highPrice: String(high),
                    consultRequired: consult,
                    consultPrice: ``,
                    offered: true,
                  }),
                ),
              ])
            },
          },
        ]}
        footer={addButton(`service`, () => setServiceDraft([...services, { ...NEW_SERVICE }]))}
        columns={[
          {
            header: `Service`,
            changed: serviceChanged([`name`]),
            width: `w-88`,
            render: (service, index) => (
              <Input
                className="shadow-none"
                aria-label="Service name"
                placeholder="e.g. Invisalign"
                value={service.name}
                onChange={(event) => serviceRow(index, { name: event.target.value })}
              />
            ),
          },
          {
            header: `Price from`,
            changed: serviceChanged([`lowPrice`]),
            width: `w-40`,
            render: (service, index) => (
              <Input
                className="shadow-none"
                aria-label="Lowest price"
                type="number"
                min={0}
                placeholder="$"
                value={service.lowPrice}
                onChange={(event) => serviceRow(index, { lowPrice: event.target.value })}
              />
            ),
          },
          {
            header: `To`,
            changed: serviceChanged([`highPrice`]),
            width: `w-40`,
            render: (service, index) => (
              <Input
                className="shadow-none"
                aria-label="Highest price"
                type="number"
                min={0}
                placeholder="$"
                value={service.highPrice}
                onChange={(event) => serviceRow(index, { highPrice: event.target.value })}
              />
            ),
          },
          {
            header: `Consult`,
            changed: serviceChanged([`consultRequired`, `consultPrice`]),
            headerInfo: CONSULT_HINT,
            render: (service, index) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  aria-label="Consult required"
                  checked={service.consultRequired}
                  onCheckedChange={(checked) => serviceRow(index, { consultRequired: checked === true })}
                />
                {service.consultRequired && (
                  <Input
                    className="shadow-none"
                    aria-label="Consult price"
                    type="number"
                    min={0}
                    placeholder="$0"
                    value={service.consultPrice}
                    onChange={(event) => serviceRow(index, { consultPrice: event.target.value })}
                  />
                )}
              </div>
            ),
          },
          {
            header: `Offered`,
            changed: serviceChanged([`offered`]),
            width: `w-24`,
            center: true,
            render: (service, index) => (
              <Switch
                aria-label="Offered"
                checked={service.offered}
                onCheckedChange={(offered) => serviceRow(index, { offered })}
              />
            ),
          },
          {
            header: ``,
            width: `w-14`,
            render: (_, index) => (
              <SectionTableTrashButton onClick={() => setServiceDraft(services.filter((__, i) => i !== index))} />
            ),
          },
        ]}
        data={services}
        rowKey={(_, index) => index}
        isNewRow={(row) => row.origin == null}
        emptyMessage="No services listed yet."
      />
    </>
  )
}
